
import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import MainLayout from '@/components/layout/MainLayout';
import { useInvoiceList } from '@/hooks/useInvoice';
import DataTable, { Column } from '@/components/ui/DataTable';
import InvoiceBatchProcess from '@/components/invoices/InvoiceBatchProcess';
import BatchList from '@/components/invoices/BatchList';
import { Invoice } from '@/lib/data';
import { Button } from '@/components/ui/button';
import { FileText, Search, Filter, ArrowDownAZ, History } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatCurrency } from '@/lib/utils';
import { useLanguage } from '@/hooks/useLanguage';
import TablePagination from '@/components/ui/TablePagination';

const Invoices = () => {
  const { translate } = useLanguage();
  const { 
    invoices, 
    isLoading, 
    searchTerm, 
    setSearchTerm, 
    pagination 
  } = useInvoiceList();
  
  // Track active tab to prevent unnecessary data loading
  const [activeTab, setActiveTab] = useState('invoices');

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM d, yyyy');
  };

  const columns: Column<Invoice>[] = [
    {
      key: 'invoice_id',
      header: translate('invoices.invoiceNumber'),
      cell: (invoice) => <span className="font-medium">{invoice.invoice_id}</span>,
      sortable: true,
    },
    {
      key: 'subscription.subscription_number',
      header: translate('subscriptions.number'),
      cell: (invoice) => (
        <span>{invoice.subscription?.subscription_number || 'N/A'}</span>
      ),
      sortable: true,
    },
    {
      key: 'subscription.customer.customer_name',
      header: translate('invoices.customer'),
      cell: (invoice) => (
        <div>
          <div>{invoice.subscription?.customer.customer_name || 'N/A'}</div>
          <div className="text-xs text-muted-foreground">
            {invoice.subscription?.customer.customer_number || 'N/A'}
          </div>
        </div>
      ),
      sortable: true,
    },
    {
      key: 'invoice_date',
      header: translate('invoices.invoiceDate'),
      cell: (invoice) => formatDate(invoice.invoice_date),
      sortable: true,
    },
    {
      key: 'status',
      header: translate('invoices.status'),
      cell: (invoice) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          invoice.status === 'draft' ? 'bg-gray-100 text-gray-800' :
          invoice.status === 'sent' || invoice.status === 'finalized' ? 'bg-blue-100 text-blue-800' :
          invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
          'bg-red-100 text-red-800'
        }`}>
          {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
        </span>
      ),
      sortable: true,
    },
    {
      key: 'total_amount',
      header: translate('invoices.amount'),
      cell: (invoice) => formatCurrency(invoice.total_amount),
      sortable: true,
      className: 'text-right',
    },
  ];

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  return (
    <MainLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold">{translate('invoices.title')}</h1>
        <p className="text-muted-foreground">{translate('invoices.subtitle')}</p>
      </div>

      <Tabs defaultValue="invoices" onValueChange={handleTabChange}>
        <TabsList className="mb-6">
          <TabsTrigger value="invoices" className="flex items-center">
            <FileText className="h-4 w-4 mr-2" />
            {translate('invoices.invoiceList')}
          </TabsTrigger>
          <TabsTrigger value="batch-process" className="flex items-center">
            <ArrowDownAZ className="h-4 w-4 mr-2" />
            {translate('invoices.batchProcessing')}
          </TabsTrigger>
          <TabsTrigger value="batches" className="flex items-center">
            <History className="h-4 w-4 mr-2" />
            {translate('invoices.batchHistory')}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="invoices" className="mt-0">
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={translate('invoices.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline">
                <Filter className="h-4 w-4 mr-2" />
                {translate('invoices.filter')}
              </Button>
            </div>
          </div>

          <div className="glass-card rounded-lg overflow-hidden">
            <DataTable
              data={invoices}
              columns={columns}
              keyField="invoice_id"
              isLoading={isLoading}
            />
          </div>

          {/* Add pagination component */}
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-muted-foreground">
              {translate('common.showing')} {invoices.length > 0 ? (pagination.page - 1) * pagination.pageSize + 1 : 0} - {Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} {translate('common.of')} {pagination.totalCount} {translate('invoices.invoices')}
            </div>
            
            <TablePagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={pagination.setPage}
            />
          </div>
        </TabsContent>
        
        <TabsContent value="batch-process" className="mt-0">
          {activeTab === 'batch-process' && <InvoiceBatchProcess />}
        </TabsContent>

        <TabsContent value="batches" className="mt-0">
          {activeTab === 'batches' && <BatchList />}
        </TabsContent>
      </Tabs>
    </MainLayout>
  );
};

export default Invoices;
